import type { Paginated } from './client';
import { ApiClient, ApiClientProps } from './client';

export interface ProductCatalog {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  items_count: number;
  processing_status?: 'pending' | 'processing' | 'completed' | 'failed';
  processing_error?: string | null;
  processing_started_at?: string | null;
  processing_completed_at?: string | null;
  source_filename?: string;
  items_created?: number;
  rows_failed?: number;
}

export interface ProductCatalogCreateInput {
  name: string;
  description?: string;
  filename?: string;
  content_type?: string;
}

export interface ProductCatalogUploadResponse extends ProductCatalog {
  signed_url: string;
  storage_key: string;
}

export interface ProductCatalogUpdateInput {
  name?: string;
  description?: string;
  is_default?: boolean;
}

/**
 * API client for product catalogs endpoints
 */
export class ProductCatalogsClient extends ApiClient {
  constructor(props: ApiClientProps) {
    super(props);
  }

  /**
   * Get all product catalogs for an organization
   * @param includeGlobal - If true, includes global catalogs in addition to org catalogs
   */
  getAll = async (organizationId: number, includeGlobal: boolean = false) => {
    const searchParams = new URLSearchParams();
    if (includeGlobal) {
      searchParams.set('include_global', 'true');
    }
    const queryString = searchParams.toString();
    const url = `organizations/${organizationId}/product-catalogs/${queryString ? `?${queryString}` : ''}`;
    return this._get<Paginated<ProductCatalog>>(url);
  };

  /**
   * Get a single product catalog by ID
   */
  getById = async (organizationId: number, catalogId: number) => {
    return this._get<ProductCatalog>(
      `organizations/${organizationId}/product-catalogs/${catalogId}/`,
    );
  };

  /**
   * Create a new product catalog
   * Returns ProductCatalogUploadResponse if filename/content_type provided
   */
  create = async (
    organizationId: number,
    data: ProductCatalogCreateInput,
  ): Promise<ProductCatalog | ProductCatalogUploadResponse> => {
    if (data.filename && data.content_type) {
      return this._post<ProductCatalogUploadResponse>(
        `organizations/${organizationId}/product-catalogs/`,
        data,
      );
    }
    return this._post<ProductCatalog>(`organizations/${organizationId}/product-catalogs/`, data);
  };

  /**
   * Update a product catalog
   */
  update = async (organizationId: number, catalogId: number, data: ProductCatalogUpdateInput) => {
    return this._patch<ProductCatalog>(
      `organizations/${organizationId}/product-catalogs/${catalogId}/`,
      data,
    );
  };

  /**
   * Delete a product catalog
   */
  delete = async (organizationId: number, catalogId: number) => {
    return this._delete(`organizations/${organizationId}/product-catalogs/${catalogId}/`);
  };

  /**
   * Upload a file to S3 using a signed URL
   * @throws {Error} If the upload fails
   */
  uploadToS3 = async (signedUrl: string, file: File) => {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 upload failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`Failed to upload file to S3: ${response.statusText} - ${errorText}`);
    }

    return response;
  };

  /**
   * Trigger processing of the uploaded catalog file
   * @throws {Error} If the API request fails
   */
  process = async (organizationId: number, catalogId: number) => {
    return this._post<{ status: string; message: string }>(
      `organizations/${organizationId}/product-catalogs/${catalogId}/process/`,
      {},
    );
  };
}
